import React from 'react';
import {
  ModalWindow,
  SwitchDialogWindows,
  Header2Text,
  BodyText,
  Grid,
  Button,
  SpruceField,
  RadioList,
  FlexRow,
  TextInput,
} from 'ui-toolkit';
import { IFetchStatus } from 'types/rest/IFetchStatus';
import { Form, Formik, FormikContextType } from 'formik';
import { ISpruceFieldProps } from 'ui-toolkit/components/SpruceField';
import styled from 'styled-components';
import { updateOrderCancellationRequestSchema } from './updateOrderCancellationRequestSchema';
import { eCancellationRequestStatus } from '../../../../ui-toolkit/orders/interfaces/eCancellationRequestStatus';
import { prettyUserType } from '../../../../ui-toolkit/orders/interfaces/eUserType';
import { IOrderCancellationRequestData } from '../../../../ui-toolkit/orders/interfaces/IOrderCancellationRequest';

interface IFormValue {
  confirm?: string;
  reject?: string;
  type: eCancellationRequestStatus;
}

interface IUpdateCancellationRequestDialogProps {
  onClose: () => void;
  onUpdate: (type: eCancellationRequestStatus, message?: string) => void;
  fetchStatus: IFetchStatus;
  fetchError: Error | null;
  cancellationRequest: IOrderCancellationRequestData;
}

export const UpdateCancellationRequestDialog: React.FC<IUpdateCancellationRequestDialogProps> = ({
  onClose,
  onUpdate,
  fetchStatus,
  fetchError,
  cancellationRequest,
}) => {
  const initialValue: IFormValue = { type: eCancellationRequestStatus.Completed };

  const onRadioButtonChange = (value: string, formik: FormikContextType<IFormValue>) => {
    if (value === eCancellationRequestStatus.Completed) {
      formik.setFieldTouched('reject');
      formik.setFieldValue('reject', null);
    } else {
      formik.setFieldTouched('confirm');
      formik.setFieldValue('confirm', null);
    }
    formik.setFieldTouched('type');
    formik.setFieldValue('type', value);
  };

  const typeOptions = (selectedValue: eCancellationRequestStatus) => [
    {
      value: eCancellationRequestStatus.Completed,
      label: (
        <div>
          Yes, cancel order.
          <SpruceField name="confirm">
            {({ field }: ISpruceFieldProps) => (
              <TextInput {...field} readOnly={selectedValue === eCancellationRequestStatus.Declined} />
            )}
          </SpruceField>
        </div>
      ),
    },
    {
      value: eCancellationRequestStatus.Declined,
      label: (
        <div>
          No, do not cancel order.{' '}
          <SpruceField name="reject">
            {({ field }: ISpruceFieldProps) => (
              <TextInput {...field} readOnly={selectedValue === eCancellationRequestStatus.Completed} />
            )}
          </SpruceField>
        </div>
      ),
    },
  ];
  return (
    <SwitchDialogWindows
      onCloseClick={onClose}
      isCompleted={fetchStatus.fetched}
      isLoading={fetchStatus.fetching}
      isError={fetchStatus.fetched && !!fetchError}
      errorMessage={fetchError && fetchError.message}
    >
      <ModalWindow
        onClose={onClose}
        title={`${prettyUserType[cancellationRequest.requesterUserType]} has required a cancellation`}
      >
        <Grid>
          <Header2Text>{`${prettyUserType[cancellationRequest.requesterUserType]} said:`}</Header2Text>
          <BodyText>{cancellationRequest.cancellationReason}</BodyText>
          <SpaceSeparator />
          <Formik
            initialValues={initialValue}
            validationSchema={updateOrderCancellationRequestSchema}
            onSubmit={(values) => {
              onUpdate(values.type, values.confirm || values.reject);
            }}
            onReset={onClose}
          >
            {({ isValid, handleSubmit }) => (
              <Form>
                <SpruceField name="type">
                  {({ field, formik }: ISpruceFieldProps) => (
                    <RadioList
                      name={'type'}
                      orientation={'horizontal'}
                      options={typeOptions(field.value)}
                      onChange={(event) => onRadioButtonChange(event.target.value, formik)}
                      value={field.value}
                    />
                  )}
                </SpruceField>
                <SpaceSeparator />
                <FlexRow justifyContent={'flex-end'}>
                  <Button type="reset" intent={'secondary'}>
                    Cancel
                  </Button>
                  <Button type="submit" intent={'primary'} disabled={!isValid}>
                    Confirm
                  </Button>
                </FlexRow>
              </Form>
            )}
          </Formik>
        </Grid>
      </ModalWindow>
    </SwitchDialogWindows>
  );
};

const SpaceSeparator = styled.div`
  height: ${(props) => `${8 * props.theme.spacing.baseValue}px`};
`;
